import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IStatisticsTableColumn } from '@services/statistics/statistics.types';
import { ColumnManagementOptionsType } from '@pages/statistics/shared/shared-statistics-functions/shared-statistics-functions.component';
import { TableService } from '@pages/statistics/shared/statistics-table/table.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ISelectOption } from '@app/shared/types/inputs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

type SubCategory = 'leads' | 'earnings' | 'others';

interface TableColumn {
  subcategory: SubCategory;
  results: IStatisticsTableColumn[];
}

interface ChangedColumn {
  checkboxItem: IStatisticsTableColumn;
  subCat: SubCategory;
}

@Component({
  selector: 'app-manage-columns-menu',
  templateUrl: './manage-columns-menu.component.html',
  styleUrls: ['./manage-columns-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageColumnsMenuComponent implements OnInit, OnDestroy {
  @Input() detailsLayout = false;
  @Input() widgetMode = false;
  @Input() savedSettings?: string[] | ISelectOption | ISelectOption[] | ColumnManagementOptionsType;

  @Output() emitSettings = new EventEmitter<ColumnManagementOptionsType>();
  @Output() closeMenu = new EventEmitter<void>();

  public warning = false;
  public isResetDisabled = false;
  public tableColumns!: ColumnManagementOptionsType;
  public clickOutside = false;
  public changedColumns: ChangedColumn[] = [];

  private readonly columnsWithSubitems: Array<'leads' | 'earnings'> = ['leads', 'earnings'];
  private readonly MAX_VISIBLE_COLUMNS = 6;
  private readonly destroy$ = new Subject<boolean>();

  constructor(
    public activeModal: NgbActiveModal,
    private readonly statisticsTableService: TableService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeTableColumns();
    this.subscribeToTableColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public trackByColumnId(_: number, item: IStatisticsTableColumn): number {
    return item.id;
  }

  public trackBySubcategory(_: number, item: TableColumn): SubCategory {
    return item.subcategory;
  }

  public applyFilters(): void {
    this.changedColumns.forEach(({ checkboxItem, subCat }) => {
      const hasSubitems = this.columnsWithSubitems.includes(subCat as 'leads' | 'earnings');
      this.tableColumns = this.handleSelectedCheckboxes(checkboxItem, subCat, this.tableColumns, this.detailsLayout, hasSubitems);
    });

    if (this.widgetMode) {
      this.emitSettings.emit(this.tableColumns);
    }

    setTimeout(() => {
      this.statisticsTableService.setTableColumns(this.tableColumns);
    });
    this.closeMenu.emit();
  }

  public onCheckboxSelect(checkboxItem: IStatisticsTableColumn, subCat: 'leads' | 'earnings' | 'others'): void {
    const existingIndex = this.changedColumns.findIndex(item => item.checkboxItem.id === checkboxItem.id);

    if (existingIndex !== -1) {
      this.changedColumns.splice(existingIndex, 1);
    } else {
      this.changedColumns.push({ checkboxItem, subCat });
    }

    this.updateWarningStates();
    this.cdr.detectChanges();
  }

  public resetFilters(): void {
    if (this.isResetDisabled) return;

    this.tableColumns = this.tableColumns.map(({ results, subcategory }) => ({
      subcategory,
      results: results.map(result => ({ ...result, checked: false }))
    }));

    this.changedColumns = [];
    this.updateWarningStates();
    this.cdr.detectChanges();
  }

  public closeMenuHandler(): void {
    this.closeMenu.emit();
  }

  public closeMenuFromOutside(): void {
    if (this.clickOutside) {
      this.closeMenuHandler();
    }
    this.clickOutside = true;
  }

  public capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private get visibleColumnsCount(): number {
    const baseCount = this.tableColumns.flatMap(({ results }) => results).filter(col => !col.onlyDetailsVisible && col.checked).length;

    const changedCount = this.changedColumns.reduce((acc, item) => {
      return acc + (item.checkboxItem.checked ? -1 : 1);
    }, 0);

    return baseCount + changedCount;
  }

  private updateWarningStates(): void {
    this.warning = this.visibleColumnsCount > this.MAX_VISIBLE_COLUMNS;
    this.isResetDisabled = this.checkIfResetDisabled();
  }

  private initializeTableColumns(): void {
    if (this.widgetMode && this.savedSettings) {
      setTimeout(() => {
        this.statisticsTableService.tableColumns$.next(this.savedSettings as ColumnManagementOptionsType);
      });
    }
  }

  private subscribeToTableColumns(): void {
    this.statisticsTableService.tableColumns$.pipe(takeUntil(this.destroy$)).subscribe(tableColumns => {
      this.tableColumns = tableColumns;
      this.updateWarningStates();
      this.cdr.detectChanges();
    });
  }

  private checkIfResetDisabled(): boolean {
    const hasCheckedColumns = this.tableColumns
      .flatMap(({ results }) => results)
      .filter(({ onlyDetailsVisible }) => !onlyDetailsVisible)
      .some(({ checked }) => checked);

    const hasChangedCheckedColumns = this.changedColumns.some(item => !item.checkboxItem.onlyDetailsVisible && item.checkboxItem.checked);

    return !hasCheckedColumns || hasChangedCheckedColumns;
  }

  private handleSelectedCheckboxes(
    checkboxItem: IStatisticsTableColumn,
    subCat: 'leads' | 'earnings' | 'others',
    tableColumns: ColumnManagementOptionsType,
    isDetailsLayout: boolean,
    hasSubitems: boolean
  ): ColumnManagementOptionsType {
    return tableColumns.map(column => {
      if (column.subcategory !== subCat) {
        return column;
      }

      return {
        ...column,
        results: column.results.map(result => {
          if (result.id !== checkboxItem.id) {
            return result;
          }

          if (isDetailsLayout && hasSubitems) {
            if (!checkboxItem.onlyDetailsVisible && checkboxItem.checked) {
              return { ...result, checked: false };
            }
            if (checkboxItem.onlyDetailsVisible && !result.onlyDetailsVisible && !result.checked) {
              return { ...result, checked: true };
            }
          }

          return { ...result, checked: !checkboxItem.checked };
        })
      };
    });
  }
}
